import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { photoEntryFromApi } from "../lib/fileUrl";
import { bindStreamToVideo, formatCameraError, requestCameraStream } from "../lib/cameraStream";
import {
  inspectionAnglesForContext,
  inspectionPhotosComplete as anglesComplete,
  inspectionPhotosPayload as anglesPayload,
} from "../lib/inspectionAngles";
import { Camera, Check, Loader, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { compressImageForUpload, formatUploadError } from "../lib/compressImage";

/**
 * Guided vehicle condition photo capture for listing baseline, pickup, and return.
 * Uploads via /api/upload and returns { angle, file_id, url } per shot.
 * Gallery uploads are disabled when liveCaptureOnly (default for inspections).
 */
export default function VehicleInspectionCapture({
  value = {},
  onChange,
  testid = "inspection-capture",
  qrToken,
  liveCaptureOnly = false,
  bookingId,
  inspectionPhase,
  seats,
  anglesRequired,
}) {
  const ANGLES = useMemo(
    () => inspectionAnglesForContext({ anglesRequired, seats }),
    [anglesRequired, seats],
  );
  const [activeAngle, setActiveAngle] = useState(ANGLES[0]?.id);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const startAbortRef = useRef(null);
  const blobRef = useRef(null);

  const captured = value || {};
  const doneCount = ANGLES.filter((a) => captured[a.id]).length;

  useEffect(() => {
    if (!ANGLES.some((a) => a.id === activeAngle)) {
      setActiveAngle(ANGLES[0]?.id);
    }
  }, [ANGLES, activeAngle]);

  const stopCamera = () => {
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
    setCameraStarting(false);
    setCameraReady(false);
    setCameraError(null);
    setPreviewUrl(null);
  };

  const attachStream = useCallback(async (video) => {
    const stream = streamRef.current;
    if (!video || !stream) return;
    setCameraReady(false);
    const ready = await bindStreamToVideo(video, stream);
    setCameraReady(ready);
    if (!ready) {
      toast.error(
        liveCaptureOnly
          ? "Camera preview didn't start — use the phone QR link instead"
          : "Camera preview didn't start — try again",
      );
    }
  }, [liveCaptureOnly]);

  const setVideoRef = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current && cameraOpen) {
      attachStream(node);
    }
  }, [attachStream, cameraOpen]);

  const startCamera = async () => {
    if (cameraStarting || cameraOpen) return;
    setCameraError(null);
    setCameraReady(false);
    setCameraStarting(true);
    startAbortRef.current?.abort();
    const abort = new AbortController();
    startAbortRef.current = abort;
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera not supported");
      const stream = await requestCameraStream({ facingMode: "environment", signal: abort.signal });
      if (abort.signal.aborted) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      setCameraOpen(true);
      if (videoRef.current) {
        await attachStream(videoRef.current);
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        const msg = formatCameraError(e, { liveCaptureOnly });
        setCameraError(msg);
        toast.error(msg);
      }
      setCameraOpen(false);
    } finally {
      if (startAbortRef.current === abort) startAbortRef.current = null;
      setCameraStarting(false);
    }
  };

  useEffect(() => () => stopCamera(), []);

  const uploadBlob = async (blob, filename) => {
    setBusy(true);
    try {
      const raw = blob instanceof File ? blob : new File([blob], filename || "photo.jpg", { type: blob.type || "image/jpeg" });
      const compressed = await compressImageForUpload(raw);
      const fd = new FormData();
      fd.append("file", compressed, compressed.name);
      let res;
      if (qrToken) {
        res = await axios.post(
          `${API}/inspections/qr-sessions/${qrToken}/upload?angle=${activeAngle}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } else {
        if (liveCaptureOnly) {
          fd.append("capture_source", "live_camera");
          if (bookingId) fd.append("booking_id", bookingId);
        }
        if (inspectionPhase) fd.append("inspection_phase", inspectionPhase);
        res = await axios.post(`${API}/upload`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const entry = photoEntryFromApi(res.data, activeAngle);
      onChange({ ...captured, [activeAngle]: entry });
      toast.success(`${ANGLES.find((a) => a.id === activeAngle)?.label} captured`);
      stopCamera();
      const next = ANGLES.find((a) => !captured[a.id] && a.id !== activeAngle);
      if (next) setActiveAngle(next.id);
      else {
        const firstMissing = ANGLES.find((a) => !captured[a.id]);
        if (firstMissing) setActiveAngle(firstMissing.id);
      }
    } catch (e) {
      toast.error(formatUploadError(e.response?.data?.detail) || e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) {
      toast.error("Camera is still starting — wait a moment and try again");
      return;
    }
    const scale = Math.min(1, 1920 / Math.max(vw, vh));
    canvas.width = Math.round(vw * scale);
    canvas.height = Math.round(vh * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        blobRef.current = blob;
        setPreviewUrl(URL.createObjectURL(blob));
      }
    }, "image/jpeg", 0.85);
  };

  const confirmCapture = () => {
    if (blobRef.current) uploadBlob(blobRef.current, `inspection-${activeAngle}.jpg`);
  };

  const current = ANGLES.find((a) => a.id === activeAngle);
  const qrSessionUrl = qrToken ? `${window.location.origin}/inspect/${qrToken}` : null;
  const gridCols = ANGLES.length > 6 ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-3 sm:grid-cols-6";

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-[var(--zc-text-3)]">
          Condition photos · {doneCount}/{ANGLES.length} angles
        </div>
        <div className="h-1.5 flex-1 max-w-[120px] ml-3 bg-[var(--zc-bg-3)] rounded-full overflow-hidden">
          <div className="h-full bg-[var(--zc-accent)] transition-all" style={{ width: `${ANGLES.length ? (doneCount / ANGLES.length) * 100 : 0}%` }} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {ANGLES.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => { stopCamera(); setActiveAngle(a.id); }}
            className={"px-3 h-8 text-xs rounded-full border font-semibold transition " +
              (activeAngle === a.id ? "border-[var(--zc-accent)] bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]" :
                captured[a.id] ? "border-[var(--zc-success)] text-[var(--zc-success)]" : "border-[var(--zc-border)] text-[var(--zc-text-3)]")}
            data-testid={`${testid}-angle-${a.id}`}
          >
            {captured[a.id] ? <Check size={10} className="inline mr-1" /> : null}
            {a.label}
          </button>
        ))}
      </div>

      {doneCount > 0 && (
        <div className={`grid ${gridCols} gap-2 mb-4`} data-testid={`${testid}-sync-grid`}>
          {ANGLES.map((a) => {
            const shot = captured[a.id];
            if (!shot?.url) {
              return (
                <div
                  key={a.id}
                  className="aspect-[4/3] rounded-lg border border-dashed border-[var(--zc-border)] bg-[var(--zc-bg-2)] flex items-center justify-center zc-meta text-center px-1"
                >
                  {a.label}
                </div>
              );
            }
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => { stopCamera(); setActiveAngle(a.id); }}
                className={"relative aspect-[4/3] rounded-lg border overflow-hidden bg-[var(--zc-bg-2)] " +
                  (activeAngle === a.id ? "border-[var(--zc-accent)] ring-2 ring-[var(--zc-accent-soft)]" : "border-[var(--zc-border)]")}
                title={a.label}
              >
                <img src={shot.url} alt={a.label} className="w-full h-full object-contain" />
                <span className="absolute bottom-0 inset-x-0 px-1 py-0.5 text-[8px] font-bold uppercase bg-black/55 text-white truncate">
                  {a.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="zc-card p-4">
        <div className="font-semibold text-sm">{current?.label}</div>
        <div className="text-xs text-[var(--zc-text-3)] mt-1">{current?.hint}</div>

        {captured[activeAngle] && !cameraOpen && (
          <div className="mt-3 relative rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)] min-h-[220px] max-h-[min(70vh,480px)] flex items-center justify-center overflow-hidden">
            <img
              src={captured[activeAngle].url}
              alt={current?.label || "Inspection photo"}
              className="w-full h-full max-h-[min(70vh,480px)] object-contain"
            />
            <button type="button" onClick={() => onChange({ ...captured, [activeAngle]: undefined })} className="absolute top-2 right-2 text-xs px-2 py-1 bg-white rounded shadow">
              Retake
            </button>
          </div>
        )}

        {!captured[activeAngle] && !cameraOpen && !cameraStarting && (
          <div className="flex flex-wrap gap-2 mt-4">
            <button type="button" onClick={startCamera} disabled={busy} className="px-4 h-10 zc-btn-primary text-xs flex items-center gap-2 disabled:opacity-50" data-testid={`${testid}-camera`}>
              <Camera size={14} /> Open camera
            </button>
          </div>
        )}
        {cameraError && !cameraOpen && !cameraStarting && (
          liveCaptureOnly ? (
            <div className="mt-3 p-3 rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)]" data-testid={`${testid}-camera-fallback`}>
              <p className="text-xs text-[var(--zc-warn)]">{cameraError}</p>
              <p className="text-xs text-[var(--zc-text-2)] mt-2 leading-relaxed">
                Live camera photos are required. If the in-page camera will not start, open the dedicated capture flow on your phone instead.
              </p>
              {qrSessionUrl && (
                <a
                  href={qrSessionUrl}
                  className="mt-3 inline-flex h-10 px-4 zc-btn-primary text-xs items-center gap-2"
                  data-testid={`${testid}-same-device-link`}
                >
                  Open capture on this phone
                </a>
              )}
            </div>
          ) : (
            <p className="text-xs text-[var(--zc-warn)] mt-3">{cameraError}</p>
          )
        )}
        {cameraStarting && (
          <div className="mt-4 rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)] p-8 flex flex-col items-center gap-3 text-center">
            <Loader size={24} className="animate-spin text-[var(--zc-accent)]" />
            <p className="text-sm font-semibold">Opening camera…</p>
            <p className="text-xs text-[var(--zc-text-3)] max-w-xs">
              If your browser asks, tap Allow. On some phones this can take up to 20 seconds.
            </p>
            {liveCaptureOnly && qrSessionUrl && (
              <p className="text-xs text-[var(--zc-text-3)] max-w-xs">
                Taking too long?{" "}
                <a href={qrSessionUrl} className="text-[var(--zc-accent)] font-semibold underline">
                  Open capture on this phone
                </a>{" "}
                instead.
              </p>
            )}
            <button type="button" onClick={stopCamera} className="mt-1 h-9 px-4 zc-btn-ghost text-xs">Cancel</button>
          </div>
        )}
        {liveCaptureOnly && !captured[activeAngle] && !cameraOpen && (
          <p className="text-xs text-[var(--zc-text-3)] mt-3">
            Live camera only — photos must be taken now. Saved gallery images are not accepted.
          </p>
        )}

        {cameraOpen && !previewUrl && (
          <div className="mt-4">
            <div className="relative rounded-lg overflow-hidden bg-black min-h-[200px] max-h-[min(70vh,480px)]">
              <video
                ref={setVideoRef}
                playsInline
                muted
                autoPlay
                className="w-full h-full max-h-[min(70vh,480px)] min-h-[200px] object-cover"
              />
              {!cameraReady && (
                <div className="absolute inset-0 grid place-items-center bg-black/80 text-white px-4 text-center">
                  <div>
                    <Loader size={22} className="animate-spin mx-auto mb-2" />
                    <p className="text-sm font-semibold">Starting camera preview…</p>
                    <p className="text-xs text-white/80 mt-1">Hold steady — the live view appears once your phone is ready.</p>
                  </div>
                </div>
              )}
            </div>
            <button type="button" onClick={captureFrame} disabled={busy || !cameraReady} className="mt-3 w-full h-11 zc-btn-primary text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader size={14} className="animate-spin" /> : <Camera size={14} />}
              {cameraReady ? `Capture ${current?.label}` : "Waiting for camera…"}
            </button>
            <button type="button" onClick={stopCamera} className="mt-2 w-full h-9 zc-btn-ghost text-xs">Cancel</button>
          </div>
        )}

        {previewUrl && (
          <div className="mt-4">
            <img src={previewUrl} alt="" className="w-full rounded-lg max-h-[min(70vh,480px)] object-contain bg-[var(--zc-bg-2)]" />
            <div className="flex gap-2 mt-3">
              <button type="button" onClick={confirmCapture} disabled={busy} className="flex-1 h-10 zc-btn-primary text-sm flex items-center justify-center gap-2">
                {busy ? <Loader size={14} className="animate-spin" /> : <Check size={14} />} Use photo
              </button>
              <button type="button" onClick={() => { setPreviewUrl(null); blobRef.current = null; }} className="h-10 px-4 zc-btn-ghost text-sm flex items-center gap-1">
                <RotateCcw size={12} /> Retake
              </button>
            </div>
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}

export function inspectionPhotosComplete(captured, anglesOrOpts) {
  if (Array.isArray(anglesOrOpts)) return anglesComplete(captured, anglesOrOpts);
  if (anglesOrOpts && typeof anglesOrOpts === "object") {
    return anglesComplete(captured, inspectionAnglesForContext(anglesOrOpts));
  }
  return anglesComplete(captured, inspectionAnglesForContext());
}

export function inspectionPhotosPayload(captured, anglesOrOpts) {
  if (Array.isArray(anglesOrOpts)) return anglesPayload(captured, anglesOrOpts);
  if (anglesOrOpts && typeof anglesOrOpts === "object") {
    return anglesPayload(captured, inspectionAnglesForContext(anglesOrOpts));
  }
  return anglesPayload(captured, inspectionAnglesForContext());
}
